/**
 * 国家选择组件
 * @author lvxinb29609
 * 入参 disabled: 是否禁用选择框
 * 入参 placeholder: 选择框的占位文本
 * 入参 className: 选择框的额外样式类
 * 入参 valueMode: value 的模式，默认为 'name'，可选值为 'code'（国家代码）和 'name'（国家对象）
 * 入参 mode: 选择框的模式，默认为 'default'，可选值为 'default' 和 'multiple'
 * 入参 value: 当前选中的国家代码，国家名称或国家对象，取决于 valueMode 的值。 mode 为 'multiple' 时，value 应为一个数组。
 * 入参 onChange: 选中国家后的回调函数，参数根据 valueMode 和 mode 的不同而不同，默认为 (name:string, country:Object) => void (第二个参数为国家Object)。multiple模式下参数均为数组。
 * 入参 showFlag: 是否显示国家旗帜，默认为 true
 * 入参 filterOption: 是否启用选项过滤功能，默认为 true
 * 入参 onSearch: 搜索输入变化时的回调函数，参数为当前搜索文本
 * 入参 maxTagCount: 当 mode 为 'multiple' 时，显示的最大标签数量，默认为 3
 * 入参 showSearch: 是否显示搜索框，默认为 true
 * 入参 allowClear: 是否允许清除选择，默认为 true
 * 入参 size: 选择框的尺寸，默认为 'middle'，可选值为 'small'、'middle' 和 'large'
 * 入参 onBlur: 选择框失去焦点时的回调函数
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import cn from 'classnames';
import { ChevronDown, Search, X } from 'lucide-react';
import { Checkbox, Input, Popover, PopoverTrigger, PopoverContent, Tag } from '@leadsnavi/ui-kit';
import {
  getCountryDisplayName,
  getCountrySearchText,
  normalizeCountry,
  normalizeValueArray,
  type CountryItem,
  type CountryRaw,
  type CountryValue,
} from '@/utils/country';
import useCountryInfoStore from '@/stores/country-info';
import { CountryFlag } from '@/common/base/country-flag';

type ValueMode = 'code' | 'name';
type SelectMode = 'default' | 'multiple';
type SizeMode = 'small' | 'smallMedium' | 'middle' | 'large';

type CountrySelectProps = {
  disabled?: boolean;
  'aria-invalid'?: boolean | 'true' | 'false';
  autoFixTag?: boolean;
  placeholder?: string;
  className?: string;
  valueMode?: ValueMode;
  mode?: SelectMode;
  value?: CountryValue | CountryValue[] | null;
  onChange?: (value: string | string[] | undefined, country: CountryRaw | CountryRaw[] | undefined) => void;
  showFlag?: boolean;
  filterOption?: boolean;
  onSearch?: (text: string) => void;
  maxTagCount?: number;
  showSearch?: boolean;
  allowClear?: boolean;
  size?: SizeMode;
  onBlur?: () => void;
};

const sizeClassMap: Record<SizeMode, string> = {
  small: 'min-h-8 text-xs px-2 py-1',
  smallMedium: 'min-h-[34px] text-xs px-2 py-1',
  middle: 'min-h-10 text-sm px-3 py-1.5',
  large: 'min-h-12 text-base px-4 py-2.5',
};

const CountrySelect = ({
  disabled = false,
  'aria-invalid': ariaInvalidProp,
  autoFixTag = true,
  placeholder = 'Select country',
  className,
  valueMode = 'name',
  mode = 'default',
  value = null,
  onChange,
  showFlag = true,
  filterOption = true,
  onSearch,
  maxTagCount = 3,
  showSearch = true,
  allowClear = true,
  size = 'middle',
  onBlur,
}: CountrySelectProps) => {
  const isInvalid = ariaInvalidProp === true || ariaInvalidProp === 'true';
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeRegion, setActiveRegion] = useState('All Regions');
  const [autoVisibleCount, setAutoVisibleCount] = useState<number | null>(null);
  const tagContainerRef = useRef<HTMLDivElement | null>(null);
  const tagMeasureRef = useRef<HTMLDivElement | null>(null);
  const { countryList, loading, fetchCountryList } = useCountryInfoStore();

  useEffect(() => {
    // 组件首次加载时获取国家列表 内部已做了缓存处理  同一个页面多次使用该组件也只会请求一次
    fetchCountryList();
  }, [fetchCountryList]);

  useEffect(() => {
    if (onSearch) {
      onSearch(searchText);
    }
  }, [onSearch, searchText]);

  const normalizedValueArray = useMemo(() => {
    const values = normalizeValueArray(value);
    if (mode === 'multiple') {
      return values;
    }
    return values.slice(0, 1);
  }, [mode, value]);

  const selectedItems = useMemo(() => {
    const fallbackItems = normalizedValueArray.map(item =>
      typeof item === 'string'
        ? normalizeCountry({
            code: valueMode === 'code' ? item : undefined,
            name: valueMode === 'name' ? item : undefined,
          })
        : normalizeCountry(item)
    );

    if (countryList.length === 0) return fallbackItems;

    return fallbackItems
      .map(item => {
        const match = countryList.find(country => {
          if (valueMode === 'code' && item.code) {
            return country.code === item.code;
          }
          if (item.name) {
            return country.name === item.name || country.enName === item.name;
          }
          return false;
        });
        return match || item;
      })
      .filter(Boolean);
  }, [countryList, normalizedValueArray, valueMode]);

  const selectedCodeSet = useMemo(() => {
    return new Set(selectedItems.map(item => item.code || getCountryDisplayName(item)));
  }, [selectedItems]);

  const regionOptions = useMemo(() => {
    const regionSet = new Set<string>();
    countryList.forEach(item => {
      if (item.region) {
        regionSet.add(item.region);
      }
    });

    const orderedRegions = ['Asia', 'Europe', 'North America', 'South America', 'Africa', 'Oceania', 'Antarctica'];

    const result = Array.from(regionSet);
    result.sort((a, b) => {
      const aIndex = orderedRegions.indexOf(a);
      const bIndex = orderedRegions.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    return ['All Regions', ...result];
  }, [countryList]);

  const filteredCountries = useMemo(() => {
    let list = countryList;
    if (activeRegion !== 'All Regions') {
      list = list.filter(item => item.region === activeRegion);
    }
    if (filterOption && searchText.trim()) {
      const keyword = searchText.trim().toLowerCase();
      list = list.filter(item => {
        return getCountrySearchText(item).includes(keyword);
      });
    }
    return list;
  }, [activeRegion, countryList, filterOption, searchText]);

  const selectAllState = useMemo<'all' | 'none' | 'indeterminate'>(() => {
    if (mode !== 'multiple' || filteredCountries.length === 0) return 'none';
    const selectedCount = filteredCountries.filter(item => {
      const key = item.code || getCountryDisplayName(item);
      return selectedCodeSet.has(key);
    }).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === filteredCountries.length) return 'all';
    return 'indeterminate';
  }, [mode, filteredCountries, selectedCodeSet]);

  const triggerLabel = useMemo(() => {
    if (mode === 'multiple') return '';
    const selected = selectedItems[0];
    return selected ? getCountryDisplayName(selected) : '';
  }, [mode, selectedItems]);

  const emitChange = useCallback(
    (items: CountryItem[]) => {
      if (!onChange) return;
      if (mode === 'multiple') {
        const values = items.map(item =>
          valueMode === 'code' ? item.code || '' : item.name || item.enName || item.code || ''
        );
        const rawItems = items.map(item => item.raw || item);
        onChange(values, rawItems);
        return;
      }

      const item = items[0];
      if (!item) {
        onChange('', undefined);
        return;
      }
      const valueOut = valueMode === 'code' ? item.code || '' : item.name || item.enName || item.code || '';
      onChange(valueOut, item.raw || item);
    },
    [mode, onChange, valueMode]
  );

  const handleSelectAll = useCallback(() => {
    if (disabled || mode !== 'multiple') return;
    const filteredKeys = new Set(filteredCountries.map(item => item.code || getCountryDisplayName(item)));
    if (selectAllState === 'all') {
      const nextItems = selectedItems.filter(item => !filteredKeys.has(item.code || getCountryDisplayName(item)));
      emitChange(nextItems);
    } else {
      const kept = selectedItems.filter(item => !filteredKeys.has(item.code || getCountryDisplayName(item)));
      emitChange([...kept, ...filteredCountries]);
    }
  }, [disabled, mode, selectAllState, filteredCountries, selectedItems, emitChange]);

  const handleSelect = useCallback(
    (item: CountryItem) => {
      if (disabled) return;
      if (mode === 'multiple') {
        const key = item.code || getCountryDisplayName(item);
        const exists = selectedCodeSet.has(key);
        const nextItems = exists
          ? selectedItems.filter(current => (current.code || getCountryDisplayName(current)) !== key)
          : [...selectedItems, item];
        emitChange(nextItems);
        return;
      }
      emitChange([item]);
      setOpen(false);
    },
    [disabled, emitChange, mode, selectedCodeSet, selectedItems]
  );

  const handleRemove = useCallback(
    (item: CountryItem) => {
      const key = item.code || getCountryDisplayName(item);
      const nextItems = selectedItems.filter(current => (current.code || getCountryDisplayName(current)) !== key);
      emitChange(nextItems);
    },
    [emitChange, selectedItems]
  );

  const handleClear = useCallback(
    (event?: MouseEvent) => {
      event?.stopPropagation();
      if (disabled) return;
      emitChange([]);
    },
    [disabled, emitChange]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (disabled) return;
      setOpen(nextOpen);
      if (!nextOpen && onBlur) {
        onBlur();
      }
    },
    [disabled, onBlur]
  );

  const hasSelection = selectedItems.length > 0;
  const effectiveTagCount = autoFixTag ? (autoVisibleCount ?? maxTagCount) : maxTagCount;
  const overflowCount =
    mode === 'multiple' && selectedItems.length > effectiveTagCount ? selectedItems.length - effectiveTagCount : 0;
  const visibleTags = mode === 'multiple' ? selectedItems.slice(0, effectiveTagCount) : [];

  const updateAutoVisibleCount = useCallback(() => {
    if (!autoFixTag || mode !== 'multiple') {
      setAutoVisibleCount(null);
      return;
    }

    const container = tagContainerRef.current;
    const measure = tagMeasureRef.current;
    if (!container || !measure) return;

    const containerWidth = container.clientWidth;
    if (!containerWidth) return;

    const gapValue = parseFloat(getComputedStyle(measure).columnGap || getComputedStyle(measure).gap || '0') || 0;
    const tagNodes = Array.from(measure.querySelectorAll('[data-tag-item="true"]')) as HTMLElement[];
    const overflowNode = measure.querySelector('[data-tag-overflow="true"]') as HTMLElement | null;
    const total = tagNodes.length;

    let usedWidth = 0;
    let count = 0;

    for (let i = 0; i < total; i += 1) {
      const tagWidth = tagNodes[i].offsetWidth;
      const nextWidth = usedWidth + (count > 0 ? gapValue : 0) + tagWidth;
      const remaining = total - (i + 1);
      const overflowWidth = remaining > 0 && overflowNode ? (count > 0 ? gapValue : 0) + overflowNode.offsetWidth : 0;

      if (nextWidth + overflowWidth <= containerWidth) {
        usedWidth = nextWidth;
        count += 1;
      } else {
        break;
      }
    }

    setAutoVisibleCount(count);
  }, [autoFixTag, mode]);

  useEffect(() => {
    updateAutoVisibleCount();
  }, [selectedItems, size, open, updateAutoVisibleCount]);

  useEffect(() => {
    if (!autoFixTag || mode !== 'multiple') return undefined;
    const container = tagContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => updateAutoVisibleCount());
    observer.observe(container);
    return () => observer.disconnect();
  }, [autoFixTag, mode, updateAutoVisibleCount]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'w-full cursor-pointer rounded-[8px] border border-input bg-white shadow-sm transition',
            'shadow-xs transition-[color,box-shadow] has-[:focus-visible]:border-ring has-[:focus-visible]:ring-ring/50 has-[:focus-visible]:ring-[3px] has-[:aria-invalid]:ring-destructive/20 has-[:aria-invalid]:border-destructive has-[:disabled]:opacity-50',
            'aria-[invalid=true]:ring-destructive/20 aria-[invalid=true]:border-destructive',
            disabled ? 'cursor-not-allowed bg-[#F7F7F7] text-[#BFBFBF]' : 'hover:border-[#C7C7C7]',
            className
          )}
          role="button"
          aria-invalid={isInvalid || undefined}
          tabIndex={disabled ? -1 : 0}
        >
          <div className={cn('flex items-center gap-2 relative', sizeClassMap[size])}>
            <div ref={tagContainerRef} className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
              {mode === 'multiple' ? (
                visibleTags.length > 0 ? (
                  visibleTags.map(item => {
                    const label = getCountryDisplayName(item);
                    return (
                      <Tag
                        key={label}
                        className="text-[#09090B] inline-flex items-center gap-1 bg-[#E5E5E5]/65"
                        variant="secondary"
                        closable={!disabled}
                        onClose={() => {
                          handleRemove(item);
                        }}
                      >
                        {label}
                      </Tag>
                    );
                  })
                ) : (
                  <span className="text-[#B3B3B3] text-sm">{placeholder}</span>
                )
              ) : hasSelection ? (
                <span className="truncate text-[#2E2E2E]">{triggerLabel}</span>
              ) : (
                <span className="text-[#B3B3B3] text-sm">{placeholder}</span>
              )}
              {overflowCount > 0 && (
                <Tag className="text-[#09090B] inline-flex items-center gap-1 bg-[#E5E5E5]/65">+{overflowCount}</Tag>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2">
              {allowClear && hasSelection && !disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[#9E9E9E] hover:text-[#6B6B6B]"
                  onClick={event => {
                    event.stopPropagation();
                    handleClear(event);
                  }}
                >
                  <X size={14} />
                </span>
              )}
              <ChevronDown size={16} className="text-[#7A7A7A]" />
            </div>
            {autoFixTag && mode === 'multiple' && selectedItems.length > 0 && (
              <div
                ref={tagMeasureRef}
                className="pointer-events-none absolute -left-[9999px] top-0 flex items-center gap-2 opacity-0"
              >
                {selectedItems.map(item => {
                  const label = getCountryDisplayName(item);
                  return (
                    <Tag
                      key={label}
                      className="text-[#09090B] inline-flex items-center gap-1 bg-[#E5E5E5]/65"
                      variant="secondary"
                      closable={!disabled}
                      onClose={() => undefined}
                      data-tag-item="true"
                    >
                      {label}
                    </Tag>
                  );
                })}
                <Tag
                  className="text-[#09090B] inline-flex items-center gap-1 bg-[#E5E5E5]/65"
                  variant="secondary"
                  data-tag-overflow="true"
                >
                  +{selectedItems.length}
                </Tag>
              </div>
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="flex max-h-[420px] w-full max-w-[354px] flex-col rounded-[8px] border border-[#EFEFEF] p-3 shadow-xl"
        side="bottom"
        align="start"
        sideOffset={8}
        style={{ zIndex: 9999 }}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {showSearch && (
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9B9B]" />
              <Input
                value={searchText}
                placeholder="Search countries..."
                className="pl-9 pr-8"
                onChange={event => setSearchText(event.target.value)}
              />
              {searchText && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9B9B9B]"
                  onClick={() => setSearchText('')}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
          {regionOptions.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {regionOptions.map(region => (
                <button
                  type="button"
                  key={region}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium transition',
                    region === activeRegion
                      ? 'bg-[#3DDC97] text-[#0B3D2E]'
                      : 'bg-[#F5F5F5] text-[#4D4D4D] hover:bg-[#EDEDED]'
                  )}
                  onClick={() => setActiveRegion(region)}
                >
                  {region}
                </button>
              ))}
            </div>
          )}
          <div
            className="min-h-0 flex-1 overflow-y-auto rounded-[8px] border border-[#F0F0F0] overscroll-contain"
            onWheel={event => event.stopPropagation()}
          >
            {loading ? (
              <div className="px-4 py-6 text-center text-sm text-[#8E8E8E]">Loading...</div>
            ) : filteredCountries.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-[#8E8E8E]">No countries found</div>
            ) : (
              <>
                {mode === 'multiple' && filteredCountries.length > 0 && (
                  <button
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition border-b border-[#F0F0F0]',
                      selectAllState === 'all' ? 'bg-[#F0FFF7]' : 'hover:bg-[#F7F7F7]'
                    )}
                    onClick={handleSelectAll}
                  >
                    <Checkbox
                      checked={
                        selectAllState === 'all' ? true : selectAllState === 'indeterminate' ? 'indeterminate' : false
                      }
                      onClick={event => {
                        event.stopPropagation();
                        handleSelectAll();
                      }}
                    />
                    <span className="text-[#2E2E2E] font-medium">All</span>
                  </button>
                )}
                {filteredCountries.map(item => {
                  const label = getCountryDisplayName(item);
                  const selectedKey = item.code || label;
                  const isSelected = selectedCodeSet.has(selectedKey);
                  return (
                    <button
                      type="button"
                      key={label}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition',
                        isSelected ? 'bg-[#F0FFF7]' : 'hover:bg-[#F7F7F7]'
                      )}
                      onClick={() => handleSelect(item)}
                    >
                      {mode === 'multiple' && (
                        <Checkbox
                          checked={isSelected}
                          onClick={event => {
                            event.stopPropagation();
                            handleSelect(item);
                          }}
                        />
                      )}
                      {showFlag && (
                        <CountryFlag countryCode={item.wmcode} />
                        // <span className="flex h-5 w-6 items-center justify-center overflow-hidden rounded-[8px] text-base">
                        //   {item.flagEmoji || item.code || label.slice(0, 2).toUpperCase()}
                        // </span>
                      )}
                      <span className="text-[#2E2E2E] font-medium">{label}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CountrySelect;
